import React, { useEffect, useState } from 'react';
import { Button, Dimmer, Label, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { maskCpfOrCnpj } from '../../../../../../services/masks';
import { translatePropertyStatus } from '../../../../util';
import {
  getClaimPortoLogs,
  updateClaimPortoLogsViewed
} from '../../../../../../services/claim';
import {
  successNotification,
  warningNotification
} from '../../../../../../services/notification';

const CLAIM_UPDATES_ACCENT_COLOR = '#59249B';
const PORTO_UNREAD_COLOR = '#00A2DD';

const InfoContainer = styled.div`
  width: 100%;
  background-color: #ffffff;
  border-left: 4px solid ${CLAIM_UPDATES_ACCENT_COLOR};
  padding: 0 0 0 12px;
  border-radius: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 0.7fr) minmax(0, 1.6fr);
  gap: 16px 24px;
  align-items: flex-start;
  color: #2f3441;
  margin: 0 0 22px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;

  > strong {
    color: #707682;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.15;
  }

  > span {
    color: #2f3441;
    font-size: 14px;
    line-height: 1.35;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

const InfoSecondary = styled.span`
  color: #707682 !important;
  font-size: 12px !important;
  line-height: 1.25 !important;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionLabel = styled.div`
  color: #2f3441;
  font-size: 14px;
  font-weight: 700;
`;

const LogsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LogCard = styled.div<{ $unread: boolean }>`
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${props => (props.$unread ? PORTO_UNREAD_COLOR : '#d1d5db')};
  border-radius: 4px;
  padding: 12px 14px;
  background: #ffffff;
`;

const LogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`;

const LogDate = styled.span`
  color: #707682;
  font-size: 12px;
`;

const LogTitle = styled.div`
  color: #2f3441;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const LogDescription = styled.div`
  color: #2f3441;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EmptyState = styled.div`
  color: #707682;
  font-size: 14px;
  padding: 24px 0;
  text-align: center;
`;

type PortoLog = {
  id: string;
  eventDate: string;
  eventTime: string;
  title: string;
  description?: string | null;
  interactionCode?: string | null;
  viewed: boolean;
};

type PortoLogsTabProps = {
  id: string;
  claim: any;
};

const formatPortoDateTime = (eventDate?: string, eventTime?: string) => {
  const raw = `${eventDate || ''}${eventTime ? ` ${eventTime}` : ''}`.trim();
  const parsed = moment(raw, ['YYYYMMDD HH:mm:ss', 'YYYYMMDD H:mm:ss'], true);
  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm:ss');
  }
  return raw || '-';
};

const PortoLogsTab = ({ id, claim }: PortoLogsTabProps) => {
  const {
    propertyStreet,
    propertyNumber,
    propertyComplement,
    propertyDistrict,
    propertyCity,
    propertyState,
    tenantName,
    tenantDocument,
    estateName,
    createdAt,
    propertyStatus
  } = claim || {};
  const [logs, setLogs] = useState<PortoLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const fullAddress = `${propertyStreet || ''}, ${propertyNumber || ''}${
    propertyComplement ? ` ${propertyComplement}` : ''
  } - ${propertyDistrict || ''} - ${propertyCity || ''} - ${propertyState || ''}`;

  useEffect(() => {
    let cancelled = false;

    const loadLogs = async () => {
      setIsLoading(true);
      try {
        const data = await getClaimPortoLogs(id);
        if (cancelled) {
          return;
        }
        const list: PortoLog[] = Array.isArray(data) ? data : [];
        const unreadCount = list.filter(item => !item.viewed).length;

        if (unreadCount > 0) {
          await updateClaimPortoLogsViewed(id, true);
          if (cancelled) {
            return;
          }
          successNotification(
            'Lido',
            `${unreadCount} mensagens da Porto marcadas como lidas!`
          );
          setLogs(list.map(item => ({ ...item, viewed: true })));
        } else {
          setLogs(list);
        }
      } catch (error) {
        if (!cancelled) {
          warningNotification(
            'Ops...',
            'Não foi possível carregar o histórico da Porto.'
          );
          setLogs([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadLogs();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleMarkUnread = async () => {
    if (!logs.length || isUpdating) {
      return;
    }
    setIsUpdating(true);
    try {
      await updateClaimPortoLogsViewed(id, false);
      setLogs(prev => prev.map(item => ({ ...item, viewed: false })));
      successNotification('Não lido', 'Histórico da Porto marcado como não lido.');
    } catch (error) {
      warningNotification('Ops...', 'Não foi possível marcar o histórico como não lido.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <Dimmer active inverted>
          <Loader content="Carregando histórico Porto..." />
        </Dimmer>
      ) : (
        <>
          <InfoContainer>
            <InfoItem>
              <strong>Inquilino</strong>
              <span>{tenantName}</span>
              {tenantDocument && (
                <InfoSecondary>{maskCpfOrCnpj(tenantDocument)}</InfoSecondary>
              )}
            </InfoItem>
            <InfoItem>
              <strong>Imobiliária</strong>
              <span>{estateName}</span>
            </InfoItem>
            <InfoItem>
              <strong>Data de abertura</strong>
              <span>{createdAt ? moment(createdAt).format('DD/MM/YYYY') : '-'}</span>
            </InfoItem>
            <InfoItem>
              <strong>Imóvel</strong>
              <span>{fullAddress}</span>
              {propertyStatus && (
                <InfoSecondary>{translatePropertyStatus(propertyStatus)}</InfoSecondary>
              )}
            </InfoItem>
          </InfoContainer>

          <ActionBar>
            <SectionLabel>Histórico Porto</SectionLabel>
            <Button
              basic
              type="button"
              disabled={!logs.length || isUpdating}
              onClick={handleMarkUnread}
              content="Marcar como não lida"
            />
          </ActionBar>

          {logs.length === 0 ? (
            <EmptyState>Nenhum histórico importado da Porto ainda.</EmptyState>
          ) : (
            <LogsList>
              {logs.map(log => (
                <LogCard key={log.id} $unread={!log.viewed}>
                  <LogHeader>
                    <LogDate>
                      {formatPortoDateTime(log.eventDate, log.eventTime)}
                    </LogDate>
                    {log.interactionCode ? (
                      <Label size="tiny" basic>
                        {log.interactionCode}
                      </Label>
                    ) : null}
                  </LogHeader>
                  <LogTitle>{log.title}</LogTitle>
                  {log.description ? (
                    <LogDescription>{log.description}</LogDescription>
                  ) : null}
                </LogCard>
              ))}
            </LogsList>
          )}
        </>
      )}
    </>
  );
};

export default PortoLogsTab;
